import { useEffect, useMemo, useState } from 'react';
import { loadNetworkCustomers, deleteNetworkCustomers } from '../../lib/networkCrmService';
import AccessDenied from './AccessDenied';
import {
  displayName,
  isMultiResto,
  filterCustomers,
  tenantsInData,
  networkStats,
  customersToCsv,
  type NetworkCustomer,
} from '../../lib/networkCrm';

/**
 * Section « CRM réseau (tous clients) » du SuperAdmin (brique B) : la base clients FINAUX
 * consolidée de tous les restos partenaires. Voir qui commande où, combien, et qui a
 * accepté la communication réseau Lil & Co — pour construire l'actif clients du groupe.
 */
export default function NetworkCrmSection() {
  const [customers, setCustomers] = useState<NetworkCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tenantId, setTenantId] = useState('all');
  const [optin, setOptin] = useState<'all' | 'network' | 'no-network'>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadNetworkCustomers().then((res) => {
      if (res.ok) setCustomers(res.customers);
      else setError(res.error ?? 'Chargement impossible.');
      setLoading(false);
    });
  }, []);

  const stats = useMemo(() => networkStats(customers), [customers]);
  const restos = useMemo(() => tenantsInData(customers), [customers]);
  const filtered = useMemo(
    () => filterCustomers(customers, { tenantId, optin, search }),
    [customers, tenantId, optin, search],
  );

  // Sélection (limitée aux clients visibles pour éviter une suppression « hors écran »).
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllVisible = () =>
    setSelected((s) => {
      const next = new Set(s);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });

  const downloadCsv = () => {
    const rows = selected.size > 0 ? filtered.filter((c) => selected.has(c.id)) : filtered;
    const csv = customersToCsv(rows);
    // BOM UTF-8 pour qu'Excel garde les accents.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `crm_reseau_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const removeSelected = async () => {
    const ids = filtered.filter((c) => selected.has(c.id)).map((c) => c.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} client(s) du CRM réseau ?`)) return;
    setDeleting(true);
    const res = await deleteNetworkCustomers(ids);
    setDeleting(false);
    if (!res.ok) {
      alert((res.error ?? 'Suppression impossible.') + '\n(Vérifie que sql/35_delete_network_customer.sql est lancé.)');
      return;
    }
    const removed = new Set(ids);
    setCustomers((list) => list.filter((c) => !removed.has(c.id)));
    setSelected(new Set());
  };

  if (loading) return <p className="opacity-70">Chargement de la base clients réseau…</p>;

  if (error) {
    return <AccessDenied title="CRM réseau (tous clients)" sqlFile="sql/23_network_crm.sql" />;
  }

  return (
    <div className="max-w-4xl">
      <div
        className="rounded-2xl p-5 mb-4 text-white"
        style={{ background: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)' }}
      >
        <div className="text-xs uppercase font-bold opacity-80">CRM réseau Lil &amp; Co</div>
        <div className="text-2xl font-extrabold">Base clients consolidée — tous restaurants</div>
        <div className="text-sm opacity-90">
          {stats.customers} clients · {stats.optinNetwork} opt-in réseau · {stats.tenants} restaurants
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Email, nom, téléphone…"
          className="flex-1 min-w-[180px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <select
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-2 text-sm"
        >
          <option value="all">Tous les restos</option>
          {restos.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <div className="flex gap-1">
          {([['all', 'Tous'], ['network', '✓ Opt-in réseau'], ['no-network', 'Resto seulement']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setOptin(id)} className={'rounded-lg px-3 py-2 text-sm font-semibold ' + (optin === id ? 'text-white' : 'bg-slate-100 text-slate-600')} style={optin === id ? { background: '#EA580C' } : {}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs opacity-60">
          {filtered.length} client(s) affiché(s)
          {selected.size > 0 && ` · ${selected.size} sélectionné(s)`}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-40"
          >
            ⬇️ CSV {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
          <button
            onClick={removeSelected}
            disabled={selected.size === 0 || deleting}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: '#DC2626' }}
          >
            {deleting ? '…' : `🗑 Supprimer (${selected.size})`}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg bg-white border border-dashed border-slate-300 p-6 text-slate-500 text-sm">
          Aucun client pour ces filtres. (La base se remplit au fil des commandes des restos.)
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase opacity-60">
              <tr>
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Tout sélectionner"
                  />
                </th>
                <th className="px-3 py-2">Resto d'origine</th>
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Téléphone</th>
                <th className="px-3 py-2">Consentements</th>
                <th className="px-3 py-2 text-right">Créé le</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const restoConsent = c.tenants.some((t) => t.marketingConsent);
                return (
                  <tr key={c.id} className={'border-t border-slate-100 ' + (selected.has(c.id) ? 'bg-amber-50' : '')}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        aria-label={`Sélectionner ${displayName(c)}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">
                      {c.tenants[0]?.name ?? '—'}
                      {isMultiResto(c) && (
                        <span className="ml-1 text-[10px] rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5">multi</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{displayName(c)}</td>
                    <td className="px-3 py-2 text-slate-600">{c.email || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.phone || '—'}</td>
                    <td className="px-3 py-2">
                      {restoConsent || c.networkConsent ? (
                        <span className="flex flex-wrap gap-1">
                          {restoConsent && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">resto</span>}
                          {c.networkConsent && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">★ réseau</span>}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">{c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
